/**
 * Админ-панель: статистика казино и управление игроками.
 */

import { useEffect, useState } from "react";

import type { AdminStatsDto, AdminUserDto, AdminWithdrawalDto } from "@koshkodrop/shared";
import { formatPaws } from "@koshkodrop/shared";

import { ApiError, api } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "../store/session";

interface Props {
  onBack: () => void;
}

const PAGE_SIZE = 30;

export function AdminPage({ onBack }: Props) {
  const me = useSession((state) => state.user);
  const setBalance = useSession((state) => state.setBalance);
  const [stats, setStats] = useState<AdminStatsDto | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [listQuery, setListQuery] = useState("");
  const [listUsers, setListUsers] = useState<AdminUserDto[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [listOffset, setListOffset] = useState(0);
  const [selected, setSelected] = useState<AdminUserDto | null>(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [withdrawalsOpen, setWithdrawalsOpen] = useState(false);
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawalDto[]>([]);
  const [withdrawalsTotal, setWithdrawalsTotal] = useState(0);
  const [withdrawalsOffset, setWithdrawalsOffset] = useState(0);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<AdminWithdrawalDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { stats: data } = await api.adminStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Нет доступа");
      }
    })();
  }, []);

  const loadUsers = async (offset = listOffset, query = listQuery) => {
    setBusy(true);
    setError(null);
    try {
      const data = await api.adminListUsers({
        q: query.trim() || undefined,
        offset,
        limit: PAGE_SIZE,
      });
      setListUsers(data.users);
      setListTotal(data.total);
      setListOffset(data.offset);
      haptic.select();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить список");
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  const openUserList = async () => {
    setListOpen(true);
    await loadUsers(0, listQuery);
  };

  const loadWithdrawals = async (offset = withdrawalsOffset) => {
    setBusy(true);
    setError(null);
    try {
      const data = await api.adminListWithdrawals({ offset, limit: PAGE_SIZE });
      setWithdrawals(data.requests);
      setWithdrawalsTotal(data.total);
      setWithdrawalsOffset(data.offset);
      haptic.select();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить выводы");
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  const openWithdrawals = async () => {
    setWithdrawalsOpen(true);
    await loadWithdrawals(0);
  };

  const refreshWithdrawal = (request: AdminWithdrawalDto) => {
    setSelectedWithdrawal(request);
    setWithdrawals((prev) => prev.map((row) => (row.id === request.id ? request : row)));
  };

  const runWithdrawalAction = async (
    action: "complete" | "refund" | "retry",
    requestId: string,
  ) => {
    setBusy(true);
    setError(null);
    try {
      const result =
        action === "complete"
          ? await api.adminCompleteWithdrawal(requestId)
          : action === "refund"
            ? await api.adminRefundWithdrawal(requestId)
            : await api.adminRetryWithdrawal(requestId);
      refreshWithdrawal(result.request);
      haptic.success();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось выполнить действие");
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  const refreshSelected = async (userId: string) => {
    const { user } = await api.adminGetUser(userId);
    setSelected(user);
    setListUsers((prev) => prev.map((row) => (row.id === user.id ? user : row)));
  };

  const toggleBlock = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const next = selected.status === "blocked" ? "active" : "blocked";
      const { user } = await api.adminSetStatus(selected.id, next);
      setSelected(user);
      setListUsers((prev) => prev.map((row) => (row.id === user.id ? user : row)));
      haptic.success();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сменить статус");
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  const applyAdjust = async () => {
    if (!selected || !adjustAmount.trim()) return;
    const normalized = adjustAmount.trim().replace(/^\u2212/, "-").replace(/^\+/, "");
    setBusy(true);
    setError(null);
    try {
      const { user } = await api.adminAdjustBalance(selected.id, normalized, adjustReason);
      setSelected(user);
      setListUsers((prev) => prev.map((row) => (row.id === user.id ? user : row)));
      if (me?.id === user.id) {
        setBalance(BigInt(user.balance));
      }
      setAdjustAmount("");
      haptic.success();
      const { stats: data } = await api.adminStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось изменить баланс");
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  const listPageCount = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));
  const listPage = Math.floor(listOffset / PAGE_SIZE) + 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl bg-night-850 px-3 py-2 text-sm font-bold text-white/60"
        >
          ← Назад
        </button>
        <h1 className="text-lg font-extrabold text-white">Админ-панель</h1>
      </div>

      {error && (
        <p className="rounded-2xl border border-lose-500/30 bg-lose-500/10 px-4 py-3 text-sm text-lose-300">
          {error}
        </p>
      )}

      {stats && (
        <section className="space-y-3 rounded-3xl border border-white/5 bg-night-850/70 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/40">
            Статистика бота
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Игроков" value={stats.userCount.toString()} />
            <Metric label="Пополнено" value={formatPaws(BigInt(stats.totalDeposited))} />
            <Metric label="На балансах" value={formatPaws(BigInt(stats.totalUserBalance))} />
            <DifferenceMetric value={BigInt(stats.difference)} label="Разница" />
            <Metric label="Оборот ставок" value={formatPaws(BigInt(stats.totalWagered))} />
            <Metric label="Бонусы выдано" value={formatPaws(BigInt(stats.totalBonuses))} />
          </div>
          <p className="text-[11px] leading-relaxed text-white/35">
            Разница = пополнено − баланс. Выдал с админки — разница 0. Слил — зелёный
            плюс. Ушёл в плюс сверх пополнений — красный минус.
          </p>
        </section>
      )}

      {stats && (
        <section className="space-y-3 rounded-3xl border border-white/5 bg-night-850/70 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/40">
            Банк казны
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Резерв" value={formatPaws(BigInt(stats.houseReserve))} />
            <DifferenceMetric value={BigInt(stats.houseLedgerNet)} label="Результат игр" />
            <Metric label="Банк" value={formatPaws(BigInt(stats.bankroll))} />
            <Metric label="Риск на ставку" value={formatPaws(BigInt(stats.maxExposure))} />
          </div>
          <p className="text-[11px] leading-relaxed text-white/35">
            Банк = резерв + результат игр − предметы в инвентарях. Риск на ставку — сколько
            казна готова проиграть на одной ставке; от него считаются лимиты во всех играх и
            доступность дорогих кейсов.
          </p>
        </section>
      )}

      <section className="rounded-3xl border border-white/5 bg-night-850/70 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/40">
            Выводы
          </h2>
          {!withdrawalsOpen && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void openWithdrawals()}
              className="rounded-xl bg-paws-500 px-3 py-1.5 text-xs font-extrabold text-night-950 disabled:opacity-40"
            >
              Заявки
            </button>
          )}
        </div>

        {withdrawalsOpen && (
          <div className="mt-3 space-y-3">
            {withdrawals.length === 0 ? (
              <p className="text-center text-sm text-white/40">Заявок пока нет</p>
            ) : (
              <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
                {withdrawals.map((request) => (
                  <li key={request.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedWithdrawal(request);
                        haptic.select();
                      }}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${
                        selectedWithdrawal?.id === request.id ? "bg-white/5" : ""
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-white">
                          {withdrawalKindLabel(request.kind)}
                        </p>
                        <p className="tabular text-xs text-white/40">
                          {request.firstName}
                          {request.username ? ` · @${request.username}` : ""}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="tabular text-xs text-paws-300">
                          {formatPaws(BigInt(request.amountPaws))}
                        </p>
                        <p className="text-[11px] font-bold text-white/40">
                          {withdrawalStatusLabel(request.status)}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {withdrawalsTotal > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  disabled={busy || withdrawalsOffset <= 0}
                  onClick={() => void loadWithdrawals(Math.max(0, withdrawalsOffset - PAGE_SIZE))}
                  className="rounded-xl border border-white/10 px-3 py-2 text-xs text-white/60 disabled:opacity-30"
                >
                  ← Назад
                </button>
                <span className="text-xs text-white/40">
                  {Math.floor(withdrawalsOffset / PAGE_SIZE) + 1} /{" "}
                  {Math.max(1, Math.ceil(withdrawalsTotal / PAGE_SIZE))}
                </span>
                <button
                  type="button"
                  disabled={busy || withdrawalsOffset + PAGE_SIZE >= withdrawalsTotal}
                  onClick={() => void loadWithdrawals(withdrawalsOffset + PAGE_SIZE)}
                  className="rounded-xl border border-white/10 px-3 py-2 text-xs text-white/60 disabled:opacity-30"
                >
                  Вперёд →
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {selectedWithdrawal && (
        <section className="space-y-3 rounded-3xl border border-white/5 bg-night-850/70 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/40">
            Заявка на вывод
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Игрок" value={selectedWithdrawal.firstName} />
            <Metric label="Доступно" value={formatPaws(BigInt(selectedWithdrawal.balance))} />
            <Metric label="В холде" value={formatPaws(BigInt(selectedWithdrawal.heldBalance))} />
            <Metric label="Сумма" value={formatPaws(BigInt(selectedWithdrawal.amountPaws))} />
            <Metric label="Статус" value={withdrawalStatusLabel(selectedWithdrawal.status)} />
          </div>
          <p className="text-xs text-white/40">
            ID {selectedWithdrawal.telegramId}
            {selectedWithdrawal.username ? ` · @${selectedWithdrawal.username}` : ""}
          </p>
          {selectedWithdrawal.contactUsername && (
            <p className="text-sm font-semibold text-paws-300">
              Связь: @{selectedWithdrawal.contactUsername}
            </p>
          )}

          {selectedWithdrawal.status === "pending" && (
            <div className="grid gap-2">
              {selectedWithdrawal.kind === "custom" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void runWithdrawalAction("complete", selectedWithdrawal.id)}
                  className="w-full rounded-xl bg-win-500/20 py-2.5 text-sm font-extrabold text-win-400 disabled:opacity-40"
                >
                  Одобрить (списать с холда)
                </button>
              )}
              {selectedWithdrawal.kind !== "custom" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void runWithdrawalAction("retry", selectedWithdrawal.id)}
                  className="w-full rounded-xl border border-paws-500/40 py-2.5 text-sm font-extrabold text-paws-300 disabled:opacity-40"
                >
                  Отправить подарок снова
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => void runWithdrawalAction("refund", selectedWithdrawal.id)}
                className="w-full rounded-xl bg-lose-500/20 py-2.5 text-sm font-extrabold text-lose-400 disabled:opacity-40"
              >
                Вернуть баланс
              </button>
            </div>
          )}
        </section>
      )}

      <section className="rounded-3xl border border-white/5 bg-night-850/70 p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-white/40">
            Игроки
          </h2>
          {!listOpen && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void openUserList()}
              className="rounded-xl bg-paws-500 px-3 py-1.5 text-xs font-extrabold text-night-950 disabled:opacity-40"
            >
              Все игроки
            </button>
          )}
        </div>

        {listOpen && (
          <div className="mt-3 space-y-3">
            <div className="flex gap-2">
              <input
                value={listQuery}
                onChange={(event) => setListQuery(event.target.value)}
                placeholder="ID, имя или @username"
                className="flex-1 rounded-xl bg-night-900 px-3 py-2.5 text-sm text-white outline-none"
                onKeyDown={(event) => {
                  if (event.key === "Enter") void loadUsers(0, listQuery);
                }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => void loadUsers(0, listQuery)}
                className="rounded-xl bg-paws-500 px-4 py-2.5 text-sm font-extrabold text-night-950 disabled:opacity-40"
              >
                Найти
              </button>
            </div>

            {listUsers.length === 0 ? (
              <p className="text-center text-sm text-white/40">Никого не найдено</p>
            ) : (
              <ul className="divide-y divide-white/5 rounded-xl border border-white/5">
                {listUsers.map((user) => {
                  const diff = BigInt(user.difference);
                  const diffTone =
                    diff > 0n ? "text-win-400" : diff < 0n ? "text-lose-400" : "text-white/40";

                  return (
                    <li key={user.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(user);
                          haptic.select();
                        }}
                        className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${
                          selected?.id === user.id ? "bg-white/5" : ""
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-white">
                            {user.firstName}
                          </p>
                          <p className="tabular text-xs text-white/40">
                            ID {user.telegramId}
                            {user.username ? ` · @${user.username}` : ""}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="tabular text-xs text-paws-300">
                            {formatPaws(BigInt(user.balance))}
                          </p>
                          <p className={`tabular text-[11px] font-bold ${diffTone}`}>
                            {formatSignedPaws(diff)}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {listTotal > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  disabled={busy || listOffset <= 0}
                  onClick={() => void loadUsers(Math.max(0, listOffset - PAGE_SIZE), listQuery)}
                  className="rounded-xl border border-white/10 px-3 py-2 text-xs text-white/60 disabled:opacity-30"
                >
                  ← Назад
                </button>
                <span className="text-xs text-white/40">
                  {listPage} / {listPageCount} · {listTotal}
                </span>
                <button
                  type="button"
                  disabled={busy || listOffset + PAGE_SIZE >= listTotal}
                  onClick={() => void loadUsers(listOffset + PAGE_SIZE, listQuery)}
                  className="rounded-xl border border-white/10 px-3 py-2 text-xs text-white/60 disabled:opacity-30"
                >
                  Вперёд →
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {selected && (
        <section className="space-y-4 rounded-3xl border border-white/5 bg-night-850/70 p-4">
          <div className="flex items-start gap-3">
            {selected.photoUrl ? (
              <img src={selected.photoUrl} alt="" className="size-12 rounded-xl object-cover" />
            ) : (
              <div className="flex size-12 items-center justify-center rounded-xl bg-night-700 text-2xl">
                😺
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-extrabold text-white">{selected.firstName}</p>
              <p className="tabular text-sm text-paws-300">ID {selected.telegramId}</p>
              {selected.username && (
                <p className="truncate text-xs text-white/40">@{selected.username}</p>
              )}
              <p className="mt-1 text-xs text-white/40">
                Статус:{" "}
                <span
                  className={
                    selected.status === "blocked" ? "text-lose-400" : "text-win-400"
                  }
                >
                  {selected.status === "blocked" ? "заблокирован" : "активен"}
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Metric label="Баланс" value={formatPaws(BigInt(selected.balance))} />
            <Metric label="Пополнил" value={formatPaws(BigInt(selected.totalDeposited))} />
            <Metric label="Оборот" value={formatPaws(BigInt(selected.totalWagered))} />
            <DifferenceMetric value={BigInt(selected.difference)} label="Разница" />
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-white/30">
              Корректировка баланса
            </p>
            <input
              value={adjustAmount}
              onChange={(event) => setAdjustAmount(event.target.value)}
              placeholder="Например: 100 или -50"
              className="w-full rounded-xl bg-night-900 px-3 py-2.5 font-mono text-sm text-white outline-none"
            />
            <input
              value={adjustReason}
              onChange={(event) => setAdjustReason(event.target.value)}
              placeholder="Причина (необязательно)"
              className="w-full rounded-xl bg-night-900 px-3 py-2.5 text-sm text-white outline-none"
            />
            <button
              type="button"
              disabled={busy || !adjustAmount.trim()}
              onClick={() => void applyAdjust()}
              className="w-full rounded-xl bg-paws-500 py-2.5 text-sm font-extrabold text-night-950 disabled:opacity-40"
            >
              Применить
            </button>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() => void toggleBlock()}
            className={`w-full rounded-xl py-2.5 text-sm font-extrabold ${
              selected.status === "blocked"
                ? "bg-win-500/20 text-win-400"
                : "bg-lose-500/20 text-lose-400"
            }`}
          >
            {selected.status === "blocked" ? "Разблокировать" : "Заблокировать"}
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => void refreshSelected(selected.id)}
            className="w-full rounded-xl border border-white/10 py-2 text-sm text-white/50"
          >
            Обновить данные
          </button>
        </section>
      )}
    </div>
  );
}

function withdrawalKindLabel(kind: AdminWithdrawalDto["kind"]): string {
  switch (kind) {
    case "ring":
      return "Кольцо 💍";
    case "trophy":
      return "Кубок 🏆";
    case "diamond":
      return "Алмаз 💎";
    case "premium_3":
      return "Premium 3 мес.";
    case "premium_6":
      return "Premium 6 мес.";
    case "premium_12":
      return "Premium 12 мес.";
    case "custom":
      return "Заявка на вывод";
    default:
      return kind;
  }
}

function withdrawalStatusLabel(status: AdminWithdrawalDto["status"]): string {
  switch (status) {
    case "pending":
      return "В обработке";
    case "completed":
      return "Выполнено";
    case "refunded":
      return "Возврат";
    case "failed":
      return "Ошибка";
    default:
      return status;
  }
}

function formatSignedPaws(value: bigint): string {
  if (value > 0n) return `+${formatPaws(value)}`;
  if (value < 0n) return `−${formatPaws(-value)}`;
  return formatPaws(0n);
}

function DifferenceMetric({ label, value }: { label: string; value: bigint }) {
  const tone = value > 0n ? "win" : value < 0n ? "lose" : "neutral";
  const color =
    tone === "win" ? "text-win-400" : tone === "lose" ? "text-lose-400" : "text-white";

  return (
    <div className="rounded-2xl border border-white/5 bg-night-900/60 px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wide text-white/30">{label}</p>
      <p className={`tabular mt-1 text-sm font-extrabold ${color}`}>
        {formatSignedPaws(value)}
      </p>
    </div>
  );
}

function Metric({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/5 bg-night-900/60 px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wide text-white/30">{label}</p>
      <p className="tabular mt-1 text-sm font-extrabold text-white">{value}</p>
    </div>
  );
}
